import React, { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import ReactSelect from "react-select";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  getEditAccount,
  getIsAccountModalOpen,
  toggleAccountModal,
  getAccountTypes,
} from "../../features/accounts/accountsSlice";
import { IFormInput, OptionsProps } from "../../types/accounts";
import { t } from "../../locales";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { filterSelectStyles } from "../../static/styles";
import { Wallet } from "lucide-react";

type Props = {
  handleSaveAccount: (data: IFormInput) => void;
};

const AccountFormModal = ({ handleSaveAccount }: Props) => {
  const dispatch = useAppDispatch();
  const editAccount = useAppSelector(getEditAccount);
  const isAccountModalOpen = useAppSelector(getIsAccountModalOpen);
  const accountTypes = useAppSelector(getAccountTypes);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [accountTypeOptions, setAccountTypeOptions] = useState<OptionsProps[]>([]);

  const {
    handleSubmit,
    register,
    reset,
    setValue,
    control,
    formState: { errors },
  } = useForm<IFormInput>({
    defaultValues: {
      account_type_id: 0,
      amount: null,
      status: null,
      description: "",
    },
  });

  const handleOnSubmit: SubmitHandler<IFormInput> = async (data: any) => {
    setIsLoading(true);
    await handleSaveAccount(data);
    handleCloseFormModal();
    setIsLoading(false);
  };

  const handleCloseFormModal = () => {
    reset();
    dispatch(toggleAccountModal(0));
  };

  useEffect(() => {
    const options =
      accountTypes?.map((at: any) => ({
        value: at.id,
        label: at.name,
      })) ?? [];
    setAccountTypeOptions(options);
  }, [accountTypes]);

  useEffect(() => {
    if (editAccount && editAccount.id) {
      reset({
        account_type_id: editAccount.account_type_id,
        amount: editAccount.amount,
        status: editAccount.status,
        description: editAccount.description,
      });
    } else {
      reset({
        account_type_id: 0,
        amount: null,
        status: isAccountModalOpen === 1 ? 0 : 1,
        description: "",
      });
    }
  }, [editAccount, isAccountModalOpen]);

  return (
    <Dialog
      open={isAccountModalOpen > 0}
      onOpenChange={(open) => {
        if (!open) handleCloseFormModal();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#113F67]">
            {isAccountModalOpen === 1
              ? t.accounts.incomeFormTitle
              : t.accounts.expenseFormTitle}
          </DialogTitle>
          <DialogDescription>
            {editAccount
              ? `Editing account #${editAccount.id}`
              : isAccountModalOpen === 1
              ? t.accounts.addIncome
              : t.accounts.addExpense}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(handleOnSubmit)} className="space-y-4 flex-1 flex flex-col sm:block pb-16 sm:pb-0 sm:space-y-5">
          <div className="space-y-2">
            <Label>{t.accounts.accountTypeLabel}</Label>
            <Controller
              name="account_type_id"
              control={control}
              rules={{ required: true, min: 1 }}
              render={({ field }) => (
                <div className="relative">
                  <Wallet className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#34699A] z-10 pointer-events-none" />
                  <ReactSelect
                    styles={filterSelectStyles}
                    placeholder={t.accounts.accountTypePlaceholder}
                    options={accountTypeOptions}
                    value={
                      accountTypeOptions.find((val) => val.value === field.value) ||
                      null
                    }
                    onChange={(val: any) => setValue("account_type_id", val?.value)}
                  />
                </div>
              )}
            />
            {errors?.account_type_id && (
              <span className="text-red-500 text-sm">
                {t.accounts.accountTypeRequired}
              </span>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount">{t.accounts.amountLabel}</Label>
            <Input
              id="amount"
              type="number"
              step="any"
              placeholder={t.accounts.amountPlaceholder}
              {...register("amount", { required: true })}
              className="h-11"
              onFocus={(e) =>
                e.target.addEventListener(
                  "wheel",
                  function (e) {
                    e.preventDefault();
                  },
                  { passive: false }
                )
              }
            />
            {errors.amount && errors.amount.type === "required" && (
              <span className="text-red-500 text-sm">
                {t.accounts.amountRequired}
              </span>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">{t.accounts.descriptionLabel}</Label>
            <Textarea
              id="description"
              rows={3}
              placeholder={t.accounts.descriptionPlaceholder}
              {...register("description", { required: false })}
            />
          </div>

          <div className="fixed bottom-0 left-0 right-0 flex gap-3 p-4 justify-end border-t bg-white z-10 sm:static sm:p-0 sm:pt-2 sm:z-auto sm:border-t sm:bg-transparent">
            <Button
              type="button"
              variant="outline"
              className="h-11 px-5 rounded-lg border-gray-300 text-[#113F67] hover:bg-gray-50 hover:text-[#113F67]"
              onClick={handleCloseFormModal}
              disabled={isLoading}
            >
              {t.accounts.cancelButton}
            </Button>
            <Button
              type="submit"
              className="h-11 px-5 rounded-lg bg-[#113F67] hover:bg-[#0d2f4f] border border-[#113F67]"
              disabled={isLoading}
            >
              {isLoading ? t.accounts.saving : t.accounts.saveButton}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AccountFormModal;
